import { useProviderAccounts } from '@/api/hooks';
import type { ProviderAccount } from '@/api/schemas';
import { stagger } from '@/lib/motion';
import { useNow } from '@/lib/useNow';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { GatedButton } from '@/components/GatedButton';
import { Icon } from '@/components/Icon';
import { Tag } from '@/components/badges';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ProviderAccountCard } from './ProviderAccountCard';
import { PROVIDED_BY_HANGAR } from './providerAccountModel';
import { useProviderAccountActions, type ProviderAccountActions } from './useProviderAccountActions';

/** Operators only; outline, so Connect Bitbucket stays the page's one primary action. */
function AddAccountButton({ actions, testId }: { actions: ProviderAccountActions; testId: string }) {
  return (
    <GatedButton variant="outline" data-testid={testId} can={actions.can} blockedReason={actions.tokenBlocked} onClick={() => actions.connect()}>
      <Icon name="add" size={18} />
      Add provider account
    </GatedButton>
  );
}

function AccountsLoading() {
  return (
    <div className="grid gap-3 p-4 sm:grid-cols-2 sm:p-5" aria-busy="true">
      {[0, 1].map((i) => (
        <Skeleton key={i} className="h-40 rounded-lg" />
      ))}
    </div>
  );
}

/** Operators register an account; a client admin learns Hangar's administrators set them up (SPEC decisions 19 and 30). */
function AccountsEmpty({ actions }: { actions: ProviderAccountActions }) {
  if (!actions.operator) {
    return <EmptyState testId="provider-accounts-empty" icon="hub" title="Your build services are on their way" description={`${PROVIDED_BY_HANGAR} Ask them if nothing shows up here.`} />;
  }
  return (
    <EmptyState
      testId="provider-accounts-empty"
      icon="hub"
      title="No provider account yet"
      description="Register the EAS account (Expo access token) that builds run on. The token is stored encrypted with your organization's key and never shown again."
      action={<AddAccountButton actions={actions} testId="provider-accounts-empty-add" />}
    />
  );
}

function AccountsList({ accounts, actions }: { accounts: ProviderAccount[]; actions: ProviderAccountActions }) {
  const now = useNow(30_000);
  return (
    <ul className="grid min-w-0 gap-3 p-4 sm:grid-cols-2 sm:p-5">
      {accounts.map((a, i) => (
        <ProviderAccountCard key={a.id} account={a} index={i} now={now} actions={actions} result={actions.results[a.id]} />
      ))}
    </ul>
  );
}

function AccountsBody({ accounts, actions }: { accounts: ReturnType<typeof useProviderAccounts>; actions: ProviderAccountActions }) {
  if (accounts.isLoading && !accounts.data) return <AccountsLoading />;
  if (accounts.isError && !accounts.data) return <ErrorState compact error={accounts.error} onRetry={() => void accounts.refetch()} />;
  if (!accounts.data || accounts.data.length === 0) return <AccountsEmpty actions={actions} />;
  return <AccountsList accounts={accounts.data} actions={actions} />;
}

/**
 * Screen 11 "Build providers" (P2 slice): EAS/Bitrise accounts with slots and health. Platform operators add, test, edit,
 * re-token and disable them (useProviderAccountActions, shared with Getting started); client admins see them read-only,
 * "Provided by Malible" (SPEC decision 19).
 */
export function ProviderAccounts() {
  const accounts = useProviderAccounts();
  const actions = useProviderAccountActions();
  return (
    <Card className="enter min-w-0" style={stagger(1)} data-testid="provider-accounts">
      <CardHeader>
        <CardTitle>Build providers{accounts.data ? ` (${accounts.data.length})` : ''}</CardTitle>
        <CardDescription>Build services Malible runs for you</CardDescription>
        <CardAction>{actions.operator ? <AddAccountButton actions={actions} testId="provider-account-add" /> : <Tag data-testid="provider-accounts-provided">Provided by Hangar</Tag>}</CardAction>
      </CardHeader>
      <CardContent className="p-0 sm:p-0">
        <AccountsBody accounts={accounts} actions={actions} />
      </CardContent>
      {actions.overlays}
    </Card>
  );
}
